import { Injectable } from "@angular/core";
import { StateService } from "./../services/state.service";
import Highcharts, { chart, each } from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import HighchartsFunnel from "highcharts/modules/funnel";
import HighchartsPareto from "highcharts/modules/pareto";
import { clone, toTitleCase } from "../utils";
HighchartsMore(Highcharts);
HighchartsFunnel(Highcharts);
HighchartsPareto(Highcharts);
import * as echarts from "echarts";
@Injectable({
  providedIn: "root",
})
export class CreateChartService {
  private registeredCharts: echarts.ECharts[] = [];
  private resizeListenerAdded = false;

  constructor(private stateService: StateService) {}

  toTitleCase(str: string): string {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  nParseFloat(val: any) {
    if (typeof val == "string") {
      return val != "" ? val : "-";
    } else if (this.isInt(val)) {
      return this.formatNumber(val);
    } else if (val && typeof val != "string" && !this.isInt(val)) {
      const floatVal: any = this.stateService.nParseFloat(val);
      return this.formatNumber(floatVal);
    } else {
      return val || "-";
    }
  }

  isInt(num: any) {
    return Number(num) === num && num % 1 === 0;
  }

  formatNumber(num: number) {
    return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
  }

  setChartData(
    edaData: any = [],
    x: any,
    y: any,
    type: any,
    bgColor: any,
    options?: any
  ) {
    const labels = edaData.map(function (el: any) {
      return el[x];
    });
    const values = edaData.map(function (el: any) {
      return type === "pie" ? el[y] : el[y]?.toFixed();
    });
    let data: any;
    if (type === "pie") {
      data = {
        labels: labels,
        datasets: [
          {
            // type: type,
            data: values,
            backgroundColor:
              bgColor.length > 0
                ? bgColor
                : [
                    "#D65623",
                    "#F9DFB0",
                    "#F49C00",
                    "#A63132",
                    "#a09ae6",
                    "#de425b",
                    "#488f31",
                  ],
            hoverBackgroundColor: ["#FFE082", "#81D4FA", "#A5D6A7"],
          },
        ],
      };
    } else if (type === "bar") {
      data = {
        labels: labels,
        datasets: [
          {
            // type: type,
            data: values,
            tension: 0.4,
            backgroundColor: "#49C2CA",
            borderColor: "#49C2CA",
          },
        ],
      };
    } else if (type === "stacked_bar") {
      data = {
        labels: labels,
        datasets: [
          {
            label: "ABI",
            backgroundColor: "#325A6D",
            borderColor: "#325A6D",
            data: [],
          },
          {
            label: "NON ABI",
            backgroundColor: "#7ED4D9",
            borderColor: "#7ED4D9",
            data: [],
          },
        ],
      };
    } else {
      data = {
        labels: labels,
        datasets: [
          {
            label:
              sessionStorage.getItem("expType") === "pho"
                ? y.toUpperCase()
                : y === "total_volume"
                  ? "Volume"
                  : "Revenue",
            data: values,
            // fill: false,
            backgroundColor: options
              ? this.createLinearGradient(0, 0, 0, 200, [
                  { offset: 0, color: "rgba(229, 182, 17, 1)" },
                  { offset: 1, color: "rgba(229, 182, 17, 0)" },
                ])
              : y === "total_revenue"
                ? this.createLinearGradient(0, 0, 0, 200, [
                    { offset: 0, color: "rgba(229, 182, 17, 1)" },
                    { offset: 1, color: "rgba(229, 182, 17, 0)" },
                  ])
                : y === "total_volume"
                  ? this.createLinearGradient(0, 0, 0, 200, [
                      { offset: 0, color: "rgba(50, 90, 109, 1)" },
                      { offset: 1, color: "rgba(255, 255, 255, 0)" },
                    ])
                  : "#E5B611",
            fill: options
              ? options.fill
              : sessionStorage.getItem("expType") === "pho"
                ? false
                : true,
            borderColor:
              y === "total_revenue"
                ? "#E5B611"
                : y === "total_volume"
                  ? "#325A6D"
                  : "#E5B611",
            borderWidth: 1,
            tension: 0.4,
          },
        ],
      };
    }
    return data;
  }

  generateColorShades(baseColor: string, count: number): string[] {
    const shades = [];
    const [r, g, b] = this.hexToRgb(baseColor);

    for (let i = 0; i < count; i++) {
      const factor = i / (count - 1); // Normalize factor between 0 and 1
      const shade = this.rgbToHex(
        Math.min(255, Math.max(0, Math.round(r + factor * (255 - r)))), // Adjust red
        Math.min(255, Math.max(0, Math.round(g + factor * (255 - g)))), // Adjust green
        Math.min(255, Math.max(0, Math.round(b + factor * (255 - b)))) // Adjust blue
      );
      shades.push(shade);
    }
    return shades;
  }

  // Convert hex to RGB
  hexToRgb(hex: string): [number, number, number] {
    hex = hex.replace(/^#/, "");

    // Parse the r, g, b values from the hex string using slice
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    return [r, g, b];
  }

  // Convert RGB to hex
  rgbToHex(r: number, g: number, b: number): string {
    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
  }

  createLinearGradient(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    colorStops: any[]
  ) {
    const ctx: any = document.createElement("canvas").getContext("2d");
    const gradient = ctx.createLinearGradient(x0, y0, x1, y1);
    colorStops.forEach((stop) =>
      gradient.addColorStop(stop.offset, stop.color)
    );
    return gradient;
  }

  setCompareChart(edaData: any = []) {
    let data: any;
    const labels = Object.keys(edaData);
    data = {
      labels: labels,
      datasets: [
        {
          label: "ABI",
          backgroundColor: "#325A6D",
          borderColor: "#325A6D",
          data: [],
        },
        {
          label: "NON ABI",
          backgroundColor: "#7ED4D9",
          borderColor: "#7ED4D9",
          data: [],
        },
      ],
    };
    labels.forEach((element) => {
      data.datasets[0].data.push(
        edaData[`${element}`]?.ABI?.revenue_contribution || 0
      );
      data.datasets[1].data.push(
        edaData[`${element}`]?.NON_ABI?.revenue_contribution || 0
      );
    });
    return data;
  }

  // Function for dynamic nesting walkway rates chart
  setLineBarCompareChart(
    densityCurve: any = [],
    walkAwayRate: any = [],
    x: any,
    y: any,
    type: any
  ) {
    let data: any;

    data = {
      labels: walkAwayRate.map((item: any) => item[x]),
      datasets: [
        {
          type: "line",
          label: "",
          borderColor: "#EE8C45",
          borderWidth: 2,
          fill: false,
          yAxisID: "y1",
          xAxisID: "x1",
          tension: 0.4,
          data: densityCurve.map((item: any) => item.density),
          pointRadius: 0, // Hide the circles
          pointHoverRadius: 0, // Hide the hover effect circles
        },
        {
          type: "bar",
          label: "",
          backgroundColor: "#684088",
          data: walkAwayRate.map((item: any) => item[y]),
          borderColor: "white",
          borderWidth: 1,
          barPercentage: 1,
          yAxisID: "y",
          categoryPercentage: 1,
        },
      ],
    };

    return data;
  }
  setRecomCompareChart(edaData: any = [], labels: any) {
    let data: any;
    let lbls: any;
    const size = edaData.length;
    lbls = [];
    edaData.forEach((element: any) => {
      lbls.push(element.cluster_name);
    });
    data = {
      labels: lbls,
      datasets: [
        {
          label: "",
          backgroundColor: "#7FD4B7",
          borderColor: "#7FD4B7",
          data: [],
          barPercentage: 1,
          borderWidth: 0,
          //  categoryPercentage: 0.5
        },
        {
          label: "",
          backgroundColor: "#706ACA",
          borderColor: "#706ACA",
          data: [],
          barPercentage: 1,
          borderWidth: 0,
          //   categoryPercentage: 0.5
        },
      ],
    };

    edaData.forEach((ele: any, j: any) => {
      labels.forEach((element: any, i: any) => {
        data.datasets[i].data.push(+ele[`${element}`]?.replace("%", ""));
      });
    });
    return data;
  }
  setSimCompareChart(edaData: any = [], labl: any, isPercentage: boolean) {
    if (edaData.length === 1) {
      let data: any;
      let labels: any;
      labels = [];
      let temp: any;
      temp = [];
      for (let index = 0; index < 3; index++) {
        temp.push(edaData[0].cluster_name);
      }

      data = {
        labels: labels,
        datasets: this.formDatas(temp),
        graphdata: [],
      };
      data.datasets.forEach((element: any) => {
        data.graphdata.push({
          cluster_name: element.clustername,
          clusterColor: element.backgroundColor,
        });
      });
      let labls = labl;
      data.labels = data.graphdata.map(function (el: any) {
        return el.cluster_name;
      });
      labls.forEach(function (value: any, j: any) {
        edaData.forEach((element: any, i: string | number) => {
          const tem = isPercentage
            ? element[`${value}`].replace("%", "")
            : element[`${value}`];
          data.datasets[j].data[j] = +tem || 0;
        });
      });
      return data;
    } else {
      let data: any;
      let labels: any;
      labels = [];
      let temp: any;
      temp = [];
      edaData.forEach((element: any) => {
        temp.push(element.cluster_name);
      });
      data = {
        labels: ["", "", ""],
        datasets: this.formData(temp),
        graphdata: [],
      };
      data.datasets.forEach((element: any) => {
        data.graphdata.push({
          cluster_name: element.clustername,
          clusterColor: element.backgroundColor,
        });
      });
      let labls = labl;

      labls.forEach(function (value: any) {
        edaData.forEach((element: any, i: string | number) => {
          const tem = isPercentage
            ? element[`${value}`].replace("%", "")
            : element[`${value}`];
          data.datasets[i].data.push(+tem || 0);
        });
      });

      return data;
    }
  }
  setSimCompareChartTop3(edaData: any = [], labl: any, isPercentage: boolean) {
    if (edaData.length === 1) {
      let data: any;
      let labels: any;
      labels = [];
      let temp: any;
      temp = [];
      labl = [...new Set(labl)];
      for (let index = 0; index < labl.length; index++) {
        temp.push(edaData[0].cluster_name);
      }

      data = {
        labels: labels,
        datasets: this.formDatas(temp),
        graphdata: [],
      };
      data.datasets.forEach((element: any) => {
        data.graphdata.push({
          cluster_name: element.clustername,
          clusterColor: element.backgroundColor,
        });
      });
      let labls = [...new Set(labl)];
      data.labels = data.graphdata.map(function (el: any) {
        return el.cluster_name;
      });
      labls.forEach(function (value: any, j: any) {
        edaData.forEach((element: any, i: string | number) => {
          if (element[`${value}`] !== undefined) {
            const tem = isPercentage
              ? element[`${value}`].replace("%", "")
              : element[`${value}`];
            data.datasets[j].data[j] = +tem || 0;
          } else {
            data.datasets[j].data[j] = 0;
          }
        });
      });
      return data;
    } else {
      let data: any;
      let labels: any;
      labels = [];
      let temp: any;
      temp = [];
      edaData.forEach((element: any) => {
        temp.push(element.cluster_name);
      });
      data = {
        labels: labels,
        datasets: this.formData(temp),
        graphdata: [],
      };
      data.datasets.forEach((element: any) => {
        data.graphdata.push({
          cluster_name: element.clustername,
          clusterColor: element.backgroundColor,
        });
      });
      let labls = [...new Set(labl)];
      data.labels = data.graphdata.map(function (el: any) {
        return el.cluster_name;
      });
      labls.forEach(function (value: any) {
        edaData.forEach((element: any, i: string | number) => {
          if (element[`${value}`] !== undefined) {
            const tem = isPercentage
              ? element[`${value}`].replace("%", "")
              : element[`${value}`];
            data.datasets[i].data[i] = +tem || 0;
          } else {
            data.datasets[i].data[i] = 0;
          }
        });
      });
      return data;
    }
  }
  formData(temp: any) {
    let tem: any;
    tem = [];
    temp.forEach((element: any, i: any) => {
      tem.push({
        label: "",
        backgroundColor: this.getAnyOne(i),
        borderColor: "#1E88E5",
        data: [],
        barPercentage: 1,
        borderWidth: 0,
        clustername: element,
        //categoryPercentage: 0.5
      });
    });
    return tem;
  }
  formDatas(temp: any) {
    let tem: any;
    tem = [];
    temp.forEach((element: any, i: any) => {
      tem.push({
        label: "",
        backgroundColor: this.getAnyOne(i),
        borderColor: "#1E88E5",
        data: [],
        barPercentage: 1,
        // categoryPercentage: 0.6,
        borderWidth: 0,
        clustername: element,
        // categoryPercentage: 1
      });
    });
    return tem;
  }
  setLineCompareChart(edaData: any = [], x: any, y: any) {
    let data: any;
    const labels = edaData[0].map(function (el: any) {
      return el[x];
    });
    const uniqlabels = [...new Set(labels)];
    let years: any[];
    years = [];
    edaData.forEach((element: any) => {
      years.push(element[0].year);
    });
    const uniq = [...new Set(years)];
    data = {
      labels: uniqlabels,
      datasets: [],
    };
    edaData.forEach((element: any, i: any) => {
      const values = element.map(function (el: any) {
        return el[y] === 0 ? null : el[y];
      });

      const val = values.map(function (el: any) {
        return el === undefined ? null : el;
      });

      const obj = {
        label: uniq[i],
        backgroundColor: this.getLineChartColor(i),
        borderColor: this.getLineChartColor(i),
        borderWidth: 0.8,
        data: val,
      };
      data.datasets.push(obj);
    });

    //  }
    return data;
  }

  getLineChartColor(index: number) {
    return [
      "#FCC7C3",
      "#D63B2F",
      "#FAA19B",
      "#F88279",
      "#F66257",
      "#F44336",
      "#B73229",
      "#992A22",
      "#7A221B",
    ][index];
  }

  getAnyOne(index: number) {
    //new design colours
    return ["#C7972A", "#F7D385", "#FDC039", "#F7E6AB", "#EED065", "#E5B629"][
      index
    ];
  }

  getRandomColor() {
    var letters = "0123456789ABCDEF";
    var color = "#";
    for (var i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }
  createWaterFallChart(
    title: string,
    data: any[],
    toolTip: any,
    type: any,
    xAxisName: string
  ) {
    let newData = [];
    let label: any = [];
    if (data[0][type] === "total") {
      const totalObj = data.shift();
      const withoutTotalData = data;
      newData = withoutTotalData;
      newData.push(totalObj);
    } else {
      newData = data;
    }
    newData.forEach((element: any) => {
      label.push(element[type]);
    });
    const option = {
      title: {
        text: "",
        subtext: "",
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: function (params: any) {
          var tar = params[1];
          var value = tar.value;
          let result;
          if (Math.abs(+value) >= 1000000000) {
            result =
              (+value / 1000000000).toFixed(1).replace(/\.0$/, "") + " B";
          } else if (Math.abs(+value) >= 1000000) {
            result = (+value / 1000000).toFixed(1).replace(/\.0$/, "") + " M";
          } else if (Math.abs(+value) >= 1000) {
            result = (+value / 1000).toFixed(1).replace(/\.0$/, "") + " K";
          } else {
            result = value;
          }
          if (title === "Volume") {
            return tar.name + "<br/>" + tar.seriesName + " : " + result;
          } else {
            return tar.name + "<br/>" + tar.seriesName + " : " + "$" + result;
          }
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        name: xAxisName,
        nameLocation: "middle",
        nameGap:
          xAxisName === "PTC SEGMENT"
            ? 60
            : xAxisName === "MANUFACTURER"
              ? 40
              : 30,
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          rotate: 30,
        },
        splitLine: { show: false },
        data: label,
      },
      yAxis: [
        {
          type: "value",
          name:
            title === "Volume" ? "Volume contribution" : "Revenue Contribution",
          nameLocation: "middle",
          nameGap: title === "Volume" ? 40 : 54,
          // min: 0,
          // max: 25,
          position: "left",
          axisLabel: {
            formatter: function (value: any) {
              let result;
              if (Math.abs(+value) >= 1000000000) {
                result =
                  (+value / 1000000000).toFixed(1).replace(/\.0$/, "") + " B";
              } else if (Math.abs(+value) >= 1000000) {
                result =
                  (+value / 1000000).toFixed(1).replace(/\.0$/, "") + " M";
              } else if (Math.abs(+value) >= 1000) {
                result = (+value / 1000).toFixed(1).replace(/\.0$/, "") + " K";
              } else {
                result = value;
              }
              if (title === "Volume") {
                return result;
              } else {
                return "$" + result;
              }
            },
          },
        },
      ],
      series: [
        {
          name: "Placeholder",
          type: "bar",
          stack: "Total",
          itemStyle: {
            borderColor: "transparent",
            color: "transparent",
          },
          emphasis: {
            itemStyle: {
              borderColor: "transparent",
              color: "transparent",
            },
          },
          data: newData.map((a: { start_point: any }) => a.start_point),
        },
        {
          name: toolTip,
          type: "bar",
          stack: "Total",
          label: {
            show: false,
            position: "inside",
            formatter: function (d: any) {
              if (Math.abs(+d.data) >= 1000000000) {
                return (
                  (+d.data / 1000000000).toFixed().replace(/\.0$/, "") + " B"
                );
              }
              if (Math.abs(+d.data) >= 1000000) {
                return (+d.data / 1000000).toFixed().replace(/\.0$/, "") + " M";
              }
              if (Math.abs(+d.data) >= 1000) {
                return (+d.data / 1000).toFixed().replace(/\.0$/, "") + " K";
              }
              return d.data;
            },
          },
          color: "#ec7063",
          data: newData.map((a: { add_points: any }) => a.add_points),
        },
      ],
    };
    return option;
  }
  createSimulationWaterFallChart(
    title: string,
    data: any[],
    toolTip: any,
    type: any
  ) {
    let newData = [];
    let label: any = [];
    if (data[0] !== undefined && data[0].label === "total") {
      const totalObj = data.shift();
      const withoutTotalData = data;
      newData = withoutTotalData;
      newData.push(totalObj);
    } else {
      newData = data;
    }
    newData.forEach((element: any) => {
      label.push(element.label);
    });
    const option = {
      title: {
        text: "",
        subtext: "",
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: function (params: any) {
          var tar = params[1];
          var value = tar.value;
          let result;
          if (Math.abs(+value) >= 1000000000) {
            result =
              (+value / 1000000000).toFixed(1).replace(/\.0$/, "") + " B";
          } else if (Math.abs(+value) >= 1000000) {
            result = (+value / 1000000).toFixed(1).replace(/\.0$/, "") + " M";
          } else if (Math.abs(+value) >= 1000) {
            result = (+value / 1000).toFixed(1).replace(/\.0$/, "") + " K";
          } else {
            result = value;
          }
          if (toolTip === "VOLUME" || toolTip === "SKU") {
            return tar.name + "<br/>" + result;
          } else {
            return tar.name + "<br/>" + "$" + result;
          }
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        name: "",
        nameLocation: "middle",
        nameGap: type === "Parent_SKU" ? 60 : 40,
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          rotate: 30,
          width: "90",
          overflow: "truncate", // or 'break' to continue in a new line
          interval: 0,
        },
        splitLine: { show: false },
        data: label,
      },
      yAxis: [
        {
          type: "value",
          name: "",
          // min: 0,
          // max: 25,
          //  position: 'left',
          nameLocation: "middle",
          nameGap: 40,
          axisLabel: {
            formatter: function (value: any) {
              let result;
              if (Math.abs(+value) >= 1000000000) {
                result =
                  (+value / 1000000000).toFixed(1).replace(/\.0$/, "") + " B";
              } else if (Math.abs(+value) >= 1000000) {
                result =
                  (+value / 1000000).toFixed(1).replace(/\.0$/, "") + " M";
              } else if (Math.abs(+value) >= 1000) {
                result = (+value / 1000).toFixed(1).replace(/\.0$/, "") + " K";
              } else {
                result = value;
              }
              if (toolTip === "VOLUME" || toolTip === "SKU") {
                return result;
              } else {
                return "$" + result;
              }
            },
          },
        },
      ],
      series: [
        {
          name: "Placeholder",
          type: "bar",
          stack: "Total",
          itemStyle: {
            borderColor: "transparent",
            color: "transparent",
          },
          emphasis: {
            itemStyle: {
              borderColor: "transparent",
              color: "transparent",
            },
          },
          data: newData.map((a: { start_point: any }) => a.start_point),
        },
        {
          name: toolTip,
          type: "bar",
          stack: "Total",
          label: {
            show: false,
            position: "top",
            formatter: function (d: any) {
              if (Math.abs(+d.data.value) >= 1000000000) {
                return (
                  (+d.data.value / 1000000000).toFixed().replace(/\.0$/, "") +
                  " B"
                );
              }
              if (Math.abs(+d.data.value) >= 1000000) {
                return (
                  (+d.data.value / 1000000).toFixed().replace(/\.0$/, "") + " M"
                );
              }
              if (Math.abs(+d.data.value) >= 1000) {
                return (
                  (+d.data.value / 1000).toFixed().replace(/\.0$/, "") + " K"
                );
              }
              return d.data.value;
            },
          },

          data: newData.map(
            (a: { add_points: any; label: any; value: any }) => {
              return {
                value: a.add_points,
                itemStyle: {
                  color: this.getFIGraphColor(a),
                },
              };
            }
          ),
        },
      ],
    };
    return option;
  }
  getFIGraphColor(key: any) {
    if (
      key.label === "Pre-Assortment" ||
      key.label === "Pre_assortment_total"
    ) {
      return "#C7972A";
    }
    if (
      key.label === "Post-Assortment" ||
      key.label === "Post_assortment_total"
    ) {
      return "#F7E6AB";
    }
    if (key.value < 0) {
      return "red";
    }
    if (
      key.label !== "Post-Assortment" &&
      key.label !== "Post_assortment_total" &&
      key.label !== "Pre-Assortment" &&
      key.label !== "Pre_assortment_total"
    ) {
      return "#E5B629";
    }
    return "#EED065";
  }

  setStackChartData(
    containerName: any,
    categories: any,
    prePortfolioData: any[] = [],
    modelOutputData: any[] = [],
    postPortfolioData: any[] = []
  ) {
    const colors = [
      "#E8D2A0",
      "#ED8269",
      "#DC5F6D",
      "#985D79",
      "#4D5175",
      "#E9C699",
      "#EA766C",
      "#C46373",
      "#8B5A7A",
      "#3E5172",
      "#EAB791",
      "#E76C6E",
      "#B06478",
      "#7F577B",
      "#2B4A6B",
      "#EBA888",
      "#E6696E",
      "#A66178",
      "#75547C",
      "#194163",
      "#ED9A7B",
      "#E3646D",
      "#A06079",
      "#605278",
      "#06365A",
    ];
    let prePortfolioValue: { name: string; data: number[]; color: string }[] =
      [];
    let modelOutputValue: { name: string; data: number[]; color: string }[] =
      [];

    let postPortfolioValue: { name: string; data: number[]; color: string }[] =
      [];

    prePortfolioData.forEach((item: any, index: any) => {
      const newItem = { ...item };
      newItem.type = "bar";
      newItem.itemStyle = {
        borderColor: "transparent",
        color: colors[index % colors.length],
      };
      prePortfolioValue.push(newItem);
    });

    modelOutputData.forEach((item: any, index: any) => {
      const newItem = { ...item };
      newItem.type = "bar";
      newItem.itemStyle = {
        borderColor: "transparent",
        color: colors[index % colors.length],
      };
      modelOutputValue.push(newItem);
    });

    postPortfolioData.forEach((item: any, index: any) => {
      const newItem = { ...item };
      newItem.type = "bar";
      newItem.itemStyle = {
        borderColor: "transparent",
        color: colors[index % colors.length],
      };
      postPortfolioValue.push(newItem);
    });

    let chartData, actualData: any;
    if (containerName === "stack-preRecommendation") {
      actualData = clone(prePortfolioValue);
      chartData = this.normalizeData(prePortfolioValue);
    } else if (containerName === "model-recommendation") {
      actualData = clone(modelOutputValue);
      chartData = this.normalizeData(modelOutputValue);
    } else if (containerName === "stack-postRecommendation") {
      actualData = clone(postPortfolioValue);
      chartData = this.normalizeData(postPortfolioValue);
    } else {
      return;
    }

    // Initialize the chart instance
    let chartDom = document.getElementById(containerName)!;

    // Check if the chart instance already exists
    let chart = echarts.getInstanceByDom(chartDom);

    if (!chart) {
      // If chart instance doesn't exist, initialize it
      chart = echarts.init(chartDom);
    } else {
      // If the chart instance already exists, clear it
      chart.clear();
    }
    // Configure the chart options
    let option = {
      title: {
        text: "", // Empty title, as per Highcharts config
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow", // highlights the entire vertical bar
        },
        formatter: (params: any) => {
          let points = "";
          let paramIndex = categories.findIndex(
            (item: any) => item == params[0].name
          );
          points += params[0].name + " <br/><br/>";
          params.forEach((each: any, index: any) => {
            points +=
              each.seriesName +
              " : <b>" +
              this.numberFormat(
                actualData[index].data[paramIndex],
                0,
                "",
                ","
              ) +
              "</b> (" +
              each.value.toFixed(0) +
              "%)<br/>";
          });
          return points;
        },
        backgroundColor: "#000000",
        textStyle: {
          color: "#ffffff",
        },
      },
      legend: {
        show: false, // Hide legend
      },
      xAxis: {
        type: "category",
        data: categories, // Categories (same as xAxis.categories in Highcharts)
        axisLabel: {
          color: "#666666",
          fontSize: 14,
          fontFamily: "Open Sans",
          interval: 0,
        },
        axisLine: {
          lineStyle: {
            color: "#EFEFEF",
          },
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: 100,
        axisLabel: {
          show: false, // This hides the labels
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: "#EFEFEF",
          },
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      series: chartData.map((series: any) => ({
        ...series,
        type: "bar", // Column (bar) type
        barWidth: 50,
        stack: "total", // Stack columns (like stacking in Highcharts)
        label: {
          show: true,
          position: "inside", // Positioning labels inside bars
          formatter: function (params: any) {
            // If the value is less than 55, hide the label
            if (params.value < 5) {
              return "";
            }
            // Otherwise, show the label with a percentage sign
            return `${params.value.toFixed(0)}%`;
          },
          color: "#323232",
          fontSize: 12,
          fontWeight: 500,
          fontFamily: "Open Sans",
        },
        emphasis: {
          focus: "none",
        },
      })),
      // Customizing data label style and tooltip, following the Highcharts version
      plotOptions: {
        bar: {
          dataLabels: {
            enabled: true,
            formatter: "{point.percentage:.0f}%", // Format data label as percentage
          },
        },
      },
    };
    // Set the option for the chart
    option && chart.setOption(option);

    this.registerChart(chart);
  }
  normalizeData(data: any) {
    // Calculate the sum of each category
    const categoryCount = data[0]?.data.length; // number of categories (Total SKUs, Volume, etc.)
    const categorySums = new Array(categoryCount).fill(0);

    // Iterate over each series
    data.forEach((series: any) => {
      // Iterate over each category in the series
      series.data.forEach((value: any, index: any) => {
        categorySums[index] += value; // Add the value of the current category to the sum
      });
    });

    // Normalize the data to percentages
    const normalizedData = data.map((series: any) => {
      return {
        ...series,
        data: series.data.map(
          (value: any, index: any) => (value / categorySums[index]) * 100
        ),
      };
    });
    return normalizedData;
  }
  setStackedWaterfallChartData(containerId: any, seriesData: any) {
    // @ts-ignore
    Highcharts.chart(containerId, {
      // @ts-ignore
      chart: {
        type: "waterfall",
      },
      title: {
        text: "",
      },
      xAxis: [
        {
          categories: seriesData.labels,
          labels: {
            style: {
              color: "#666666",
              fontSize: "14px",
              fontFamily: "Open Sans",
            },
          },
          lineColor: "#EFEFEF",
          lineWidth: 1,
          axisLabel: {
            color: "#666666",
            fontSize: 14,
            fontFamily: "Open Sans",
            rotate: 45, // Rotate the labels to make space for long labels
            interval: 0, // Display every label
          },
        },
      ],
      yAxis: {
        min: 0,
        title: {
          text: "",
        },
        labels: {
          enabled: false,
          format: "{value}%",
          style: {
            color: "#666666",
            fontSize: "14px",
            fontFamily: "Open Sans",
          },
        },
        gridLineWidth: 1,
        gridLineColor: "#EFEFEF",
      },
      plotOptions: {
        series: {
          stacking: "normal",
          borderWidth: 0,
          borderRadius: 0,
        },
      },
      tooltip: {
        backgroundColor: "#000000",
        style: {
          color: "#ffffff",
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      series: seriesData.data,
    });
  }

  setGroupedBarChartData(containerName: any, labels: any, barData: any) {
    // Convert labels to title case
    let updatedLabels = labels.map((label: any) => this.toTitleCase(label));
    // Initialize the chart instance
    let chart = echarts.init(document.getElementById(containerName));
    // Configure the chart options
    let echartSeries = barData.map((item: any) => {
      return {
        name: item.name,
        type: "bar",
        barGap: 0,
        itemStyle: {
          borderColor: "transparent",
          color: item.color,
        },
        data: item.data,
      };
    });
    let option = {
      title: {
        text: "",
        left: "left",
      },
      legend: {
        data: barData.map((data: any) => data.name),
        show: false,
      },
      tooltip: {
        trigger: "item", // Show tooltip for each individual item (bar)
        backgroundColor: "#000000",
        textStyle: {
          color: "#ffffff",
        },
        formatter: function (params: any) {
          // The tooltip shows the series name and the value in millions
          return `<b>${params.seriesName}</b>: ${params.value.toFixed(2)}`;
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: updatedLabels,
        axisLine: {
          lineStyle: {
            color: "#EFEFEF",
          },
        },
        axisLabel: {
          color: "#666666",
          fontSize: 14,
          fontFamily: "Open Sans",
          interval: 0, // Display every label
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        axisLine: {
          show: true, // Show the Y-axis line
          lineStyle: {
            width: 1, // Y-axis line width (optional)
            type: "solid", // Y-axis line style (optional)
            color: "#EFEFEF", // White axis line
          },
        },
        axisLabel: {
          color: "#666666",
          fontSize: 14,
          fontFamily: "Open Sans",
          formatter: function (value: number) {
            // Format the Y-axis value as millions
            if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + "M";
            }
            return value;
          },
        },
      },
      series: echartSeries,
    };

    // Set the option for the chart
    option && chart.setOption(option);
    this.registerChart(chart);
  }

  setWaterfallChartData(
    containerName: string,
    data?: any,
    data1?: any,
    data2?: any,
    topCount: number = 0,
    bottomCount: number = 0
  ) {
    let chartData: any = [];
    let minValue: any;
    let markAreas: any = {},
      markAreas1: any = {};
    const calculateMinValue = (
      data: any,
      preName: string,
      upliftName: string
    ) => {
      const pre = data?.find((d: any) => d.name === preName)?.y;
      const negativeUplift = data?.find((d: any) => d.name === upliftName)?.y;
      const percentageNegativeUplift = (negativeUplift / pre) * 100;
      const adjustedPercentage = percentageNegativeUplift + 10;
      const finalValue = (adjustedPercentage / 100) * pre;
      return finalValue * 10;
    };

    const calculateSecondMaxYValue = (data2: any) => {
      const yValues = data2.map((item: any) => item.y);
      const uniqueSortedYValues = [...new Set(yValues)].sort(
        (a: any, b: any) => b - a
      );
      return uniqueSortedYValues[1];
    };

    if (
      containerName === "macoUpliftChart" ||
      containerName.includes("macoUpliftChart")
    ) {
      chartData = data;
      if (data) {
        minValue = calculateMinValue(data, "Pre", "Negative Uplift");
      }
    } else if (
      containerName === "volumeUpliftChart" ||
      containerName.includes("volumeUpliftChart")
    ) {
      chartData = data1;
      if (data1) {
        minValue = calculateMinValue(data1, "Pre", "Volume Delisted");
      }
    } else if (containerName === "macoAnalysisChart") {
      chartData = data2;
      if (data2) {
        minValue = calculateSecondMaxYValue(data2);
      }

      // Add the top markArea if topCount is greater than 0
      if (topCount > 0) {
        markAreas = {
          data: [
            [
              {
                name: "Top Positive Uplift",
                xAxis: chartData[1].name, // Start at Wednesday (0.5)
              },
              {
                xAxis: chartData[topCount].name,
              },
            ],
          ],
          itemStyle: {
            color: "rgba(76, 182, 76, 0.04)", // Light green color for the marked area
            borderColor: "#EFEFEF",
            borderWidth: 2,
            borderType: "dotted",
          },
          tooltip: {
            // Disable tooltip for markArea
            show: false,
          },
          label: {
            show: true,
            position: "top",
            formatter: "Top Positive Uplift", // Text displayed in the middle of the mark area
            fontWeight: 600,
            fontSize: 14,
            distance: 20,
            color: "#666666", // Label color
          },
        };
      }
      chartData && chartData.splice(topCount + 1, 0, { name: "", y: 0 });

      if (bottomCount > 0) {
        markAreas1 = {
          data: [
            [
              {
                name: "Botton Negative Uplift",
                xAxis: chartData[topCount + 2].name, // Start at Wednesday (0.5)
              },
              {
                xAxis: chartData[topCount + 2 + bottomCount].name,
              },
            ],
          ],
          itemStyle: {
            color: "rgba(230, 120, 120, 0.04)", // Light green color for the marked area
            borderColor: "#EFEFEF",
            borderWidth: 2,
            borderType: "dotted",
          },
          tooltip: {
            // Disable tooltip for markArea
            show: false,
          },
          label: {
            show: true,
            position: "top",
            formatter: "Bottom Negative Uplift", // Text displayed in the middle of the mark area
            fontWeight: 600,
            fontSize: 14,
            distance: 20,
            color: "#666666", // Label color
          },
        };
      }
    } else if (containerName.includes("revenueUpliftChart")) {
      chartData = data2;
      if (data2) {
        minValue = calculateMinValue(data2, "Pre", "Negative Uplift");
      }
    } else {
      return;
    }

    let placeholderSeries = [0];
    if (!chartData) {
      return;
    }
    let copiedData = clone(chartData);
    copiedData?.forEach((item: any, index: any) => {
      if (!item.isIntermediateSum && !item.isSum) {
        placeholderSeries.push(item?.y + placeholderSeries[index]);
        //for negative values
        if (item?.y < 0) {
          placeholderSeries[index] = placeholderSeries[index] + item?.y;
          item.y = Math.abs(item?.y);
        }
      }

      //intermediate sum and sum calculation
      if (item.isIntermediateSum || item.isSum) {
        placeholderSeries[index] = 0;

        item.y = 0;
        copiedData.forEach((element: any, cindex: any) => {
          if (cindex < index && !element.isIntermediateSum) {
            item.y += data[cindex]?.y;
          }
        });
        placeholderSeries.push(item?.y);
      }
    });
    placeholderSeries.pop();
    let maxHeight = Math.max(...placeholderSeries.map(Number));
    const maxIndex = placeholderSeries.indexOf(maxHeight);
    let maxValue = maxHeight + copiedData[maxIndex]?.y;

    if (containerName === "macoAnalysisChart") {
      placeholderSeries.splice(topCount + 1, 1, 0);
      maxValue = Math.floor(maxValue / 100000 + 1) * 100000;
    } else {
      maxValue = Math.floor(maxValue / 1000000 + 1) * 1000000;
    }

    // Initialize the chart instance
    let chartDom = document.getElementById(containerName)!;

    // Check if the chart instance already exists
    let chart = echarts.getInstanceByDom(
      document.getElementById(containerName)!
    );

    if (!chart) {
      // If chart instance doesn't exist, initialize it
      chart = echarts.init(chartDom);
    } else {
      // If the chart instance already exists, clear it
      chart.clear();
    }

    let option = {
      title: {
        text: "",
      },
      grid: {
        left: containerName === "macoAnalysisChart" ? "5%" : "20%", // Adjust this value to shrink the chart area and add space for labels
        right: "3%",
        top: "10%",
        bottom: "10%",
      },
      tooltip: {
        trigger: "item",
        formatter: (params: any) => {
          const colorDot = `<span style="color: ${params.color}">&#9679;</span>`;
          return `<b>${colorDot} ${params.name} : ${this.numberFormat(params.value, 0, "", ",")}</b>`;
        },
        fontWeight: 500,
        backgroundColor: "#000000",
        textStyle: {
          color: "#ffffff",
        },
      },
      xAxis: {
        type: "category",
        data: chartData.map((item: any) => item.name),
        axisLine: {
          lineStyle: {
            color: "#EFEFEF", // White axis line
          },
        },
        axisLabel: {
          show: true,
          rotate: 0,
          interval: 0, // Display all labels
          formatter: function (value: string) {
            return value.replace(/ /g, "\n"); // Split after spaces for line breaks
          },
          textStyle: {
            color: "#666666", // White axis labels
          },
          fontSize: 12, // Font size for labels
          lineHeight: 16, // Adjust the line height to prevent overlap
        },
      },
      yAxis: {
        type: "value",
        min: minValue > 1000000 ? minValue - 1000000 : minValue,
        max: maxValue,
        axisLine: {
          show: true,
          lineStyle: {
            color: "#EFEFEF",
          },
        },
        axisLabel: {
          show: true,
          // rotate: 45, // Rotate labels by 45 degrees
          margin: 20, // Add more space between yAxis labels and chart area
          interval: 0,
          textStyle: {
            color: "#666666", // White axis labels
          },
          formatter: (value: number) => {
            if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + "M";
            } else if (value >= 1000) {
              return (value / 1000).toFixed(1) + "K";
            }
            return value;
          },
        },
      },
      series: [
        {
          name: "Placeholder",
          type: "bar",
          stack: "waterfall",
          barWidth: function (params: any) {
            // If the value is 0, reduce the bar width
            return params.name === "" ? 1 : 20; // Set a small width for 0 values (2px), otherwise 30px
          }, // Set the width of the bars
          barGap: "0%",
          silent: true,
          itemStyle: {
            borderColor: "transparent",
            color: "transparent",
          },
          emphasis: {
            itemStyle: {
              borderColor: "transparent",
              color: "transparent",
            },
          },
          data: placeholderSeries,
          markArea: markAreas1,
        },
        {
          name: "Waterfall",
          type: "bar",
          stack: "waterfall",
          data: chartData.map((item: any) => {
            return {
              value: Math.abs(item.y),
              itemStyle: { color: item.color },
            };
          }),
          markArea: markAreas,
          barMinHeight: 30, // Adjust this value as needed
          barWidth: function (params: any) {
            // If the value is 0, reduce the bar width
            return params.name === "" ? 1 : 20; // Set a small width for 0 values (2px), otherwise 30px
          }, // Set the width of the bars
          barGap: "0%",
          label: {
            show: true, // Show the labels
            position: "top", // Position the label inside the bar
            color: "#000", // Label color (optional)
            fontSize: 12, // Label font size (optional)
            fontWeight: 500,
            formatter: (params: any) => {
              let data = chartData[params?.dataIndex].y;
              if (Math.abs(data) >= 1000000) {
                return (data / 1000000).toFixed(1) + "M";
              } else if (Math.abs(data) >= 1000) {
                return (data / 1000).toFixed(1) + "K";
              } else if (data === 0) {
                return "";
              }
              return data.toFixed(1);
            },
          },
        },
      ],
    };
    option && chart.setOption(option);
    chart.resize();
    this.registerChart(chart);
  }

  setBarChartData(containerName: any, demandTransferData: any) {
    //@ts-ignore
    Highcharts.chart(containerName, {
      //@ts-ignore
      chart: {
        type: "column",
      },
      title: {
        text: "",
        align: "left",
      },
      xAxis: {
        categories: demandTransferData.labels,
        crosshair: false,
        labels: {
          style: {
            color: "#666666",
            fontSize: "14px",
            fontFamily: "Open Sans",
          },
        },
        lineColor: "#EFEFEF",
        lineWidth: 1,
      },
      yAxis: {
        min: 0,
        max: demandTransferData?.maxYaxisValue,
        title: {
          text: "",
        },
        labels: {
          style: {
            color: "#666666",
            fontSize: "14px",
            fontFamily: "Open Sans",
          },
        },
        gridLineWidth: 1,
        gridLineColor: "#EFEFEF",
      },
      tooltip: {
        valueSuffix: "",
        backgroundColor: "#000000",
        style: {
          color: "#ffffff",
        },
      },
      plotOptions: {
        column: {
          pointPadding: 0.05,
          groupPadding: 0,
          borderWidth: 0,
          grouping: false,
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      series: demandTransferData.datasets,
    });
  }

  getJourneyChartData(data: any, container: any) {
    data = data.map((item: any) =>
      item.name == "Delist Pipeline SKUs"
        ? { ...item, name: "Delist Pipeline Executed" }
        : item
    );

    // Set intermediate sum and sum points
    data?.forEach((item: any, index: number, array: any[]) => {
      item.isIntermediateSum = index === 2;
      item.isSum = index === array.length - 1;
    });

    let primaryData = data.map((item: any) => ({
      name: item.name,
      y: item.y ?? 0,
      color: "#D69E77",
      isSum: item.isSum,
      isIntermediateSum: item.isIntermediateSum,
    }));

    if (primaryData[3]) primaryData[3].color = "#959B7B";
    if (primaryData[4]) primaryData[4].color = "#959B7B";

    let secondaryData = data.map((item: any) => ({
      name: item.name,
      y: item.y1 ?? 0,
      color: "#325A6D",
      isSum: item.isSum,
      isIntermediateSum: item.isIntermediateSum,
    }));

    //@ts-ignore
    Highcharts.chart(container, {
      //@ts-ignore
      chart: { type: "waterfall" },
      title: { text: "" },
      xAxis: {
        type: "category",
        labels: {
          style: {
            color: "#666666",
            fontSize: "14px",
            fontFamily: "Open Sans",
            whiteSpace: "normal",
            textOverflow: "none",
          },
          rotation: 0,
          autoRotation: false,
          overflow: "allow",
          useHTML: true,
        },
        minPadding: 0,
        maxPadding: 0,
        lineColor: "#EFEFEF",
        lineWidth: 1,
      },
      yAxis: {
        title: { text: "" },
        labels: {
          style: {
            color: "#666666",
            fontSize: "12px",
            fontFamily: "Open Sans",
          },
        },
        lineColor: "#EFEFEF",
        lineWidth: 1,
      },
      legend: { enabled: false },
      credits: { enabled: false },
      tooltip: {
        formatter: function () {
          if (!this.points) return "";

          let primaryPoint = this.points.find(
            (p) => p.series.name === "Primary"
          );
          let secondaryPoint = this.points.find(
            (p) => p.series.name === "Secondary"
          );

          let primaryDot = primaryPoint
            ? `<span style="color: ${primaryPoint.color}">&#9679;</span>`
            : "";
          let secondaryDot = secondaryPoint
            ? `<span style="color: ${secondaryPoint.color}">&#9679;</span>`
            : "";

          let primaryText =
            primaryPoint?.y != null
              ? `${primaryDot} Active SKUs: ${Highcharts.numberFormat(primaryPoint.y, 0, "", ",")}`
              : "";

          let secondaryText =
            secondaryPoint?.y != null
              ? ` ${secondaryDot} Delist Pipeline: ${Highcharts.numberFormat(secondaryPoint.y, 0, "", ",")}`
              : "";

          return `<b>${this.point.name}</b><br/>${primaryText}<br/>${secondaryText}`;
        },
        shared: true,
        backgroundColor: "#000000",
        style: { color: "#ffffff" },
      },
      plotOptions: {
        series: { stacking: "normal" }, // Enable stacking
      },
      series: [
        ...(secondaryData.some((item: any) => item.y !== 0) // Adding secondary series only if y1 values exist
          ? [
              {
                name: "Secondary", // Delist Pipeline (Bottom First)
                type: "waterfall",
                data: secondaryData,
                borderWidth: 0,
                borderRadius: 0,
                dataLabels: {
                  enabled: true,
                  inside: true,
                  verticalAlign: "middle",
                  align: "center",
                  y: 0,
                  crop: false,
                  overflow: "none",
                  style: {
                    color: "#000000",
                    fontSize: "12px",
                    fontFamily: "Open Sans",
                    fontWeight: 500,
                    textOutline: "none",
                  },
                  formatter: function () {
                    return this.y === 0 ? "" : this.y;
                  },
                },
              },
            ]
          : []),
        {
          name: "Primary", // Active SKUs (On Top)
          type: "waterfall",
          data: primaryData,
          borderWidth: 0,
          borderRadius: 0,
          dataLabels: {
            enabled: true,
            inside: true,
            verticalAlign: "middle",
            align: "center",
            y: 0,
            crop: false,
            overflow: "none",
            style: {
              color: "#000000",
              fontSize: "12px",
              fontFamily: "Open Sans",
              fontWeight: 500,
              textOutline: "none",
            },
            formatter: function () {
              return this.y === 0 ? "" : this.y;
            },
          },
        },
      ],
    });
  }

  setTailAnalysisChartData1(
    containerName: string,
    firstTailAnalysisChart: any[],
    secondTailAnalysisChart: any[],
    thirdTailAnalysisChart: any[],
    fourthTailAnalysisChart: any[]
  ) {
    let apiData: any, brands;

    if (containerName === "first-tail") {
      brands = firstTailAnalysisChart[0];
      apiData = firstTailAnalysisChart[1];
    } else if (containerName === "second-tail") {
      brands = secondTailAnalysisChart[0];
      apiData = secondTailAnalysisChart[1];
    } else if (containerName === "third-tail") {
      brands = thirdTailAnalysisChart[0];
      apiData = thirdTailAnalysisChart[1];
    } else if (containerName === "fourth-tail") {
      brands = fourthTailAnalysisChart[0];
      apiData = fourthTailAnalysisChart[1];
    }

    let chartData = this.normalizeData(clone(apiData));

    console.log(chartData);

    const entries = brands.length;
    const baseHeight = 450; // for 10 entries
    const chartHeight = Math.ceil(entries / 10) * baseHeight;
    console.log(chartHeight);

    // Initialize the chart instance
    let chartDom = document.getElementById(containerName)!;

    chartDom.style.height = chartHeight + "px";

    // Check if the chart instance already exists
    let chart = echarts.getInstanceByDom(chartDom);

    // Wait a bit to let DOM apply new height
    setTimeout(() => {
      chart?.resize();
    }, 50);

    if (!chart) {
      // If chart instance doesn't exist, initialize it
      chart = echarts.init(chartDom);
    } else {
      // If the chart instance already exists, clear it
      chart.clear();
    }

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: function (params: any) {
          console.log(params);
          let str = `${params[0].name}<br/><br/>`;
          params.forEach((item: any, index: any) => {
            const percent = item.value.toFixed(2);
            const value = apiData[index].data[item.dataIndex];
            str += `${toTitleCase(item.seriesName)}: <b>${value}</b> (${percent}%)<br/>`;
          });
          return str;
        },
        backgroundColor: "#000000",
        textStyle: {
          color: "#ffffff",
          fontSize: 14,
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "8%",
        top: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        axisLine: { lineStyle: { color: "#EFEFEF" } },
        axisLabel: { show: false },
        splitLine: {
          lineStyle: { color: "#EFEFEF" },
        },
      },
      yAxis: {
        type: "category",
        data: brands,
        axisLine: { lineStyle: { color: "#EFEFEF" } },
        axisLabel: {
          color: "#666666",
          fontSize: 14,
          fontFamily: "Open Sans",
        },
        inverse: true,
      },
      series: chartData.map((series: any) => ({
        ...series,
        type: "bar",
        stack: "total",
        label: {
          show: true,
          position: "inside",
          formatter: (params: any) => {
            const p = params.data;
            if (p === 0 || p < 2) return "";
            return `${p.toFixed(1)}%`;
          },
          color: "#000000",
          fontSize: 14,
          fontFamily: "Open Sans",
          fontWeight: 500,
        },
        emphasis: {
          focus: "none",
        },
      })),
      legend: { show: false },
    };

    chart.setOption(option);
    window.addEventListener("resize", () => chart?.resize());
  }

  // stacked waterfall chart for executive summar
  getStackedJourneyChartData(data: any, container: any) {
    let apiData = clone(data);

    // Set intermediate sum and sum points
    data?.forEach((item: any, index: number, array: any[]) => {
      item.isIntermediateSum = index === 2;
      item.isSum = index === array.length - 1;
    });

    let placeholderSeries = [0];
    data?.forEach((item: any, index: any) => {
      if (!item.isIntermediateSum && !item.isSum) {
        placeholderSeries.push(item?.y + item?.y1 + placeholderSeries[index]);
        //for negative values
        if (item?.y + item?.y1 < 0) {
          placeholderSeries[index] =
            placeholderSeries[index] + (item?.y + item?.y1);
          if (item?.y < 0) {
            item.y = Math.abs(item?.y);
          }
          if (item?.y1 < 0) {
            item.y1 = Math.abs(item?.y1);
          }
        }
      }

      //intermediate sum and sum calculation
      if (item.isIntermediateSum || item.isSum) {
        placeholderSeries[index] = 0;

        item.y = 0;
        item.y1 = 0;
        data.forEach((element: any, cindex: any) => {
          if (cindex < index && !element.isIntermediateSum) {
            item.y += apiData[cindex]?.y;
            item.y1 += apiData[cindex]?.y1;
          }
        });
        placeholderSeries.push(item?.y + item?.y1);
      }
    });

    const categories = data.map((item: any) => item?.name);
    let seriesDataY = data.map((item: any) => Math.abs(item?.y));
    let seriesDataY1 = data.map((item: any) => Math.abs(item?.y1));

    // Removed SKU Count change
    placeholderSeries.splice(1, 1);
    seriesDataY.splice(1, 1);
    seriesDataY1.splice(1, 1);
    categories.splice(1, 1);

    let chartDom = document.getElementById(container);
    let myChart = echarts.init(chartDom);
    let option = {
      title: {
        text: "",
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: function (params: any) {
          // Check if there are multiple series
          let primaryPoint = params.find(
            (p: any) => p.seriesName === "Active SKUs"
          );
          let secondaryPoint = params.find(
            (p: any) => p.seriesName === "Delist Pipeline"
          );

          let primaryDot = primaryPoint
            ? `<span style="color: ${primaryPoint.color}">&#9679;</span>`
            : "";
          let secondaryDot = secondaryPoint
            ? `<span style="color: ${secondaryPoint.color}">&#9679;</span>`
            : "";

          let primaryText =
            primaryPoint && primaryPoint.value != null
              ? `${primaryDot} Active SKUs: ${Math.abs(primaryPoint.value).toLocaleString()}`
              : "";

          let secondaryText =
            secondaryPoint && secondaryPoint.value != null
              ? ` ${secondaryDot} Delist Pipeline: ${Math.abs(secondaryPoint.value).toLocaleString()}`
              : "";
          if (
            params[0].name === "New innovation SKUs" ||
            params[0].name === "New non-innovation SKUs" ||
            params[0].name === "Delist Pipeline SKUs" ||
            params[0].name === "Delisted SKUs"
          ) {
            return `<b>${params[0].name} : ${Math.abs(primaryPoint.value !== 0 ? primaryPoint.value : secondaryPoint.value).toLocaleString()}</b>`;
          }
          return `<b>${params[0].name}</b><br/>${primaryText}<br/>${secondaryText}`;
        },
        backgroundColor: "#000000",
        textStyle: { color: "#ffffff" },
        shared: true,
      },
      legend: {
        show: false, // Hides the legend
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: categories,
        axisLine: {
          lineStyle: {
            color: "#EFEFEF", // White axis line
          },
        },
        axisLabel: {
          show: true,
          interval: 0, // Display all labels
          formatter: function (value: any) {
            return value.replaceAll(" ", "\n"); // Join them with newline for wrapping
          },
          textStyle: {
            color: "#666666", // White axis labels
          },
          fontSize: 12, // Font size for labels
          lineHeight: 16, // Adjust the line height to prevent overlap
        },
      },
      yAxis: {
        type: "value",
        axisLine: {
          show: true, // Show the Y-axis line
          lineStyle: {
            width: 1, // Y-axis line width (optional)
            type: "solid", // Y-axis line style (optional)
            color: "#EFEFEF", // White axis line
          },
        },
        axisLabel: {
          textStyle: {
            color: "#666666", // White axis labels
          },
        },
      },
      series: [
        {
          name: "Placeholder",
          type: "bar",
          stack: "Total",
          barWidth: 25, // Set the width of the bars
          barGap: "50%",
          silent: true,
          itemStyle: {
            borderColor: "transparent",
            color: "transparent",
          },
          emphasis: {
            itemStyle: {
              borderColor: "transparent",
              color: "transparent",
            },
          },
          data: placeholderSeries,
        },
        {
          name: "Active SKUs",
          type: "bar",
          stack: "Total",
          barWidth: 25, // Set the width of the bars
          barGap: "50%",
          label: {
            show: true, // Show the labels
            position: "inside", // Position the label inside the bar
            verticalAlign: "middle", // Vertically align the label to the middle of the bar
            align: "center", // Horizontally align the label to the center of the bar
            color: "#000", // Label color (optional)
            fontSize: 12, // Label font size (optional)
            fontWeight: 500,
            formatter: function (params: any) {
              return params.value === 0 ? "" : Math.abs(params.value); // Hide label if value is 0
            },
          },
          itemStyle: {
            borderColor: "transparent",
            color: "#D69E77",
          },
          data: seriesDataY,
        },
        {
          name: "Delist Pipeline",
          type: "bar",
          stack: "Total",
          barWidth: 25, // Set the width of the bars
          barGap: "50%",
          label: {
            show: true, // Show the labels
            position: "inside", // Position the label inside the bar
            verticalAlign: "middle", // Vertically align the label to the middle of the bar
            align: "center", // Horizontally align the label to the center of the bar
            color: "#000", // Label color (optional)
            fontSize: 12, // Label font size (optional)
            fontWeight: 500,
            formatter: function (params: any) {
              return params.value === 0 ? "" : Math.abs(params.value);
            },
          },
          itemStyle: {
            borderColor: "transparent",
            color: "#325A6D",
          },
          data: seriesDataY1,
        },
      ],
    };
    option && myChart.setOption(option);
    // Ensure the chart resizes with the window
    this.registerChart(myChart);
  }

  registerChart(chart: echarts.ECharts) {
    this.registeredCharts.push(chart);
    this.addResizeListener(); // only adds once
  }

  private addResizeListener() {
    if (this.resizeListenerAdded) return;

    const debounce = (func: () => void, wait: number) => {
      let timeout: any;
      return () => {
        clearTimeout(timeout);
        timeout = setTimeout(func, wait);
      };
    };

    const onResize = debounce(() => {
      this.registeredCharts.forEach((chart) => {
        chart.resize();
      });
    }, 300);

    window.addEventListener("resize", onResize);
    this.resizeListenerAdded = true;
  }

  numberFormat(
    value: any,
    decimals = 0,
    decimalPoint = ".",
    thousandsSep = ","
  ) {
    // Convert value to number if it's a string
    value = Number(value);

    // If the value is NaN, return it as is
    if (isNaN(value)) {
      return value;
    }

    // Round the value to the specified decimal places
    const fixedValue = value.toFixed(decimals);

    // Split the value into the integer and decimal parts
    let [integerPart, decimalPart] = fixedValue.split(".");

    // Add thousands separators to the integer part
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);

    // If there are decimals, join the integer and decimal parts, else just return the integer part
    if (decimals > 0) {
      return `${integerPart}${decimalPoint}${decimalPart}`;
    } else {
      return integerPart;
    }
  }
  getDashboardStackedJourneyChartData(data: any, container: string) {
    const colors = [
      "#E8D2A0",
      "#ED8269",
      "#DC5F6D",
      "#985D79",
      "#4D5175",
      "#E9C699",
      "#EA766C",
      "#C46373",
      "#8B5A7A",
      "#3E5172",
      "#EAB791",
      "#E76C6E",
      "#B06478",
      "#7F577B",
      "#2B4A6B",
      "#EBA888",
      "#E6696E",
      "#A66178",
      "#75547C",
      "#194163",
      "#ED9A7B",
      "#E3646D",
      "#A06079",
      "#605278",
      "#06365A",
    ];

    const keySet = Object.keys(data);
    console.log(data);
    const placeholderSeries = new Array(data[keySet[0]].length).fill(0);

    placeholderSeries.forEach((item: any, index: any, arr: any) => {
      let sum1 = 0,
        sum2 = 0;
      if (index !== 0 && index !== 2 && index !== arr.length - 1) {
        keySet.forEach((key: any) => {
          sum1 += data[key][index - 1].y;
          sum2 += data[key][index].y;
        });
        placeholderSeries[index] =
          placeholderSeries[index - 1] +
          (sum1 < 0 ? 0 : sum1) +
          (sum2 < 0 ? sum2 : 0);
      }

      if (index === 2 || index === arr.length - 1) {
        placeholderSeries[index] = 0;
        keySet.forEach((key: any) => {
          const total = data[key]
            .slice(0, index)
            .filter((_: any, i: any) => i !== 2)
            .reduce((acc: number, val: any) => acc + val.y, 0);
          data[key][index].y = total;
        });
      }
    });

    // 👇️ Remove index 1 from data and placeholderSeries
    const removeIndex = 1;
    keySet.forEach((key) => {
      data[key].splice(removeIndex, 1);
    });
    placeholderSeries.splice(removeIndex, 1);

    const categories = data[keySet[0]].map((item: any) => item.name);

    const series = keySet.map((group, index) => ({
      name: group,
      type: "bar",
      stack: "total",
      data: data[group].map((item: any) => Math.abs(item.y)),
      barWidth: 40,
      barGap: "50%",
      label: {
        show: true,
        position: "inside",
        verticalAlign: "middle",
        align: "center",
        color: "#000",
        fontSize: 12,
        fontWeight: 500,
        formatter: (params: any) => (params.value === 0 ? "" : params.value),
      },
      itemStyle: {
        borderColor: "transparent",
        color: colors[index % colors.length],
      },
    }));

    const chartDom = document.getElementById(container);
    
    // Check if the chart instance already exists
    let chart = echarts.getInstanceByDom(document.getElementById(container)!);

    if (!chart) {
      // If chart instance doesn't exist, initialize it
      chart = echarts.init(chartDom);
    } else {
      // If the chart instance already exists, clear it
      chart.clear();
    }

    const option = {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: any) => {
          let tooltip = `<b>${params[0].name}</b>`;
          params.forEach((item: any) => {
            if (item.seriesName !== "Placeholder") {
              tooltip += `<br><span style="color:${item.color}">&#9679;</span> ${item.seriesName}: ${Math.abs(item.value).toLocaleString()}`;
            }
          });
          return tooltip;
        },
        backgroundColor: "#000",
        textStyle: { color: "#fff" },
        shared: true,
      },
      legend: { show: false },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: categories,
        axisLine: { lineStyle: { color: "#EFEFEF" } },
        axisLabel: {
          show: true,
          interval: 0,
          formatter: (value: string) => value.replace(/ /g, "\n"),
          textStyle: {
            color: "#666",
            fontSize: 12,
            lineHeight: 16,
          },
        },
      },
      yAxis: {
        type: "value",
        axisLine: {
          show: true,
          lineStyle: {
            width: 1,
            type: "solid",
            color: "#EFEFEF",
          },
        },
        axisLabel: { textStyle: { color: "#666" } },
      },
      series: [
        {
          name: "Placeholder",
          type: "bar",
          stack: "total",
          barWidth: 40,
          barGap: "50%",
          silent: true,
          itemStyle: {
            color: "transparent",
            borderColor: "transparent",
          },
          emphasis: {
            itemStyle: {
              color: "transparent",
              borderColor: "transparent",
            },
          },
          data: placeholderSeries,
        },
        ...series,
      ],
    };

    chart.setOption(option);
    this.registerChart(chart);
  }
}
